import React from 'react';
import { useRevalidator } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/solid';

import { messageFromError, messageOfFailure } from '@oysterjs/core/graphql/errors';
import { Description, ErrorMessage, Label } from '@oysterjs/ui/fieldset';
import { Spinner } from '@oysterjs/ui/spinner';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import {
  AmsNotLoggedReason,
  IntegrationDetailFieldsFragment,
  IntegrationFieldsFragment,
  useSetIntegrationLogsSentEmailsMutation
} from '~/api/graphql';
import { useAmsLoggingEnabled } from '~/components/use-ams-logging-enabled';
import { integrationName } from '~/lib/integrations';

const SAVED_TICK_MS = 2_000;

/**
 * The switch that logs emails sent from Cara to this AMS. IntegrationManagePage builds it
 * for AMS panels only, since the server refuses logging for anything else.
 */
export const SentEmailLoggingSwitch = ({
  integration,
  emailLoggingIntegration,
  canUpdate,
  disabled = false,
  onSavingChange
}: {
  integration: IntegrationDetailFieldsFragment;
  /** The agency's integration that logs sent emails, if any. */
  emailLoggingIntegration: IntegrationFieldsFragment | undefined;
  canUpdate: boolean;
  disabled?: boolean;
  onSavingChange?: (saving: boolean) => void;
}) => {
  const isAmsLoggingEnabled = useAmsLoggingEnabled();
  const revalidator = useRevalidator();
  const [setIntegrationLogsSentEmails] = useSetIntegrationLogsSentEmailsMutation();
  // The value being saved, shown until the reloaded integration catches up with it.
  const [savingLogsSentEmails, setSavingLogsSentEmails] = React.useState<boolean>();
  const [isSaving, setIsSaving] = React.useState(false);
  const [showSaved, setShowSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string>();
  const savedTickTimer = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => setSavingLogsSentEmails(undefined), [integration.logsSentEmails]);
  React.useEffect(() => () => clearTimeout(savedTickTimer.current), []);

  if (!isAmsLoggingEnabled) {
    return null;
  }

  const amsName = integrationName(integration.type);
  const logsSentEmails = savingLogsSentEmails ?? integration.logsSentEmails;
  const isUnsupported = !integration.canLogSentEmails;
  const otherLoggingIntegration =
    emailLoggingIntegration?.id === integration.id ? undefined : emailLoggingIntegration;
  const canTurnOn = !isUnsupported && !otherLoggingIntegration;
  // Turning it off always works, so an AMS that lost support can still be switched off.
  const isLocked = disabled || isSaving || !canUpdate || (!logsSentEmails && !canTurnOn);

  const isUnsupportedConnection =
    integration.cannotLogSentEmailsReason === AmsNotLoggedReason.UnsupportedConnection;
  const description = isUnsupportedConnection
    ? `${amsName} logs emails only through an SDK connection. To turn this on, choose SDK under Update credentials.`
    : isUnsupported
      ? `${amsName} isn't supported yet.`
      : otherLoggingIntegration
        ? `Emails are already logged to ${integrationName(otherLoggingIntegration.type)}. Turn that off first to log to ${amsName} instead.`
        : `When an email from Cara goes to an address that matches exactly one ${amsName} client, Cara adds it to that client's record. Agents can log the rest from Mailbox > Outgoing. Cara can't remove an email once it's logged.`;

  const handleToggle = async (enabled: boolean) => {
    clearTimeout(savedTickTimer.current);
    setShowSaved(false);
    setSaveError(undefined);
    setSavingLogsSentEmails(enabled);
    setIsSaving(true);
    onSavingChange?.(true);

    const loggingStateAfterFailure = enabled ? 'off' : 'on';
    const couldNotSave = `Couldn't save. Logging is still ${loggingStateAfterFailure}. Try again.`;
    let failure: string | null;
    try {
      const result = await setIntegrationLogsSentEmails({
        variables: { id: integration.id, enabled }
      });
      failure = messageOfFailure(result, couldNotSave);
    } catch (err) {
      failure = messageFromError(err, couldNotSave);
    }

    setIsSaving(false);
    onSavingChange?.(false);
    if (failure) {
      setSavingLogsSentEmails(undefined);
      setSaveError(failure);
    } else {
      setShowSaved(true);
      savedTickTimer.current = setTimeout(() => setShowSaved(false), SAVED_TICK_MS);
    }
    // Reloads the panel and the cards, so they show what the server holds even after a
    // refusal.
    void revalidator.revalidate();
  };

  return (
    <SwitchField>
      <div className="flex items-center gap-2" data-slot="label">
        <Label>Log emails sent from Cara to {amsName}</Label>
        <div className="flex items-center gap-1.5" aria-live="polite">
          {isSaving && (
            <>
              <Spinner size="size-3.5" color="neutral" />
              <span className="sr-only">Saving</span>
            </>
          )}
          {showSaved && (
            <>
              <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
              <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
            </>
          )}
        </div>
      </div>
      <Description>{description}</Description>
      <Switch checked={logsSentEmails} onChange={handleToggle} disabled={isLocked} color="sky" />
      {(saveError || !canUpdate) && (
        // Inside the field so the switch is described by these lines too.
        <div className="col-start-1 row-start-3 mt-1 space-y-1">
          {saveError && <ErrorMessage role="alert">{saveError}</ErrorMessage>}
          {!canUpdate && (
            <Description>Only people who can manage integrations can change this.</Description>
          )}
        </div>
      )}
    </SwitchField>
  );
};

/** The switch under a Sync Settings heading of its own, for AMS panels that have none. */
export const SentEmailLoggingSection = ({ children }: { children: React.ReactNode }) => {
  const isAmsLoggingEnabled = useAmsLoggingEnabled();
  if (!isAmsLoggingEnabled) {
    return null;
  }
  return (
    <div className="mt-4 space-y-4">
      <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Sync Settings</h3>
      {children}
    </div>
  );
};
